import { useQuery } from "@tanstack/react-query";
import api from "../../api/apiClient";
import { apiPost, apiDelete } from "../../utils/api";
import { StoreStatus } from "../../types/enum/storeStatus";
import {
  InCompleteStore,
  StoreAdmin,
  StoreAdminDetails,
  StoreWithoutDetails,
} from "../../types/modules/admin/StoreAdmin.type";

interface UseParams {
  page?: number;
  limit?: number;
  status: StoreStatus;
  name?: string;
  brandName?: string;
  phone?: string;
  commercialRegister?: string;
  city?: string;
  type?: string;
  subType?: string;
  dateFilter?: string;
  mode?: string;
  onlyRanked?: boolean;
  autoAccept?: string;
  isComingSoon?: string;
}

export const useStoresByAdmin = ({
  page = 1,
  limit = 9,
  status,
  name = "",
  brandName = "",
  phone = "",
  commercialRegister = "",
  city = "",
  type = "",
  subType = "",
  dateFilter = "",
  mode = "",
  onlyRanked = false,
  autoAccept = "",
  isComingSoon = "",
}: UseParams) => {
  const queryParams = new URLSearchParams({
    page: page.toString(),
    limit: limit.toString(),
    status: status,
    name: name,
    brandName: brandName,
    phone: phone,
    commercialRegister: commercialRegister,
    city: city,
    type: type,
    subType: subType,
    dateFilter: dateFilter,
    mode: mode,
    onlyRanked: onlyRanked.toString(),
    autoAccept: autoAccept,
    isComingSoon: isComingSoon,
  });
  return useQuery({
    queryKey: [
      "stores-by-admin",
      page,
      limit,
      status,
      name,
      brandName,
      phone,
      commercialRegister,
      city,
      type,
      subType,
      dateFilter,
      mode,
      onlyRanked,
      autoAccept,
      isComingSoon,
    ],
    queryFn: () =>
      api.get<{ stores: StoreAdmin[]; totalItems: number; totalPages: number }>(`store/admin/all?${queryParams.toString()}`).then(res => res.data),
  });
};

export const useStorDetailsByAdmin = (
  storeId: number,
  enabled: boolean = true
) => {
  return useQuery({
    queryKey: ["store-detalis-by-admin", storeId],
    queryFn: () =>
      api.get<StoreAdminDetails>(`store/admin/${storeId}/details`).then(res => res.data),
    enabled: enabled && !!storeId,
  });
};

interface UseParams2 {
  page?: number;
  limit?: number;
}

export const useStoresInCompleteByAdmin = ({
  page = 1,
  limit = 10,
}: UseParams2) => {
  const queryParams = new URLSearchParams({
    page: page.toString(),
    limit: limit.toString(),
  });
  return useQuery({
    queryKey: ["stores-incompletes-by-admin", limit, page],
    queryFn: () =>
      api.get<{ stores: InCompleteStore[]; total: number; totalPages: number }>(`store/admin/all/incomplete?${queryParams.toString()}`).then(res => res.data),
  });
};


export interface StoreFinancials {
  availableBalance: number;
  totalCanceled: number;
  totalRefunded: number;
  totalEarning: number;
  totalAdminCommission: number;
  totalVisaCommission: number;
  totalMadaCommission: number;
  totalWalletCommission: number;
  totalZakatAmount: number;
}

export const useStoreFinancials = (
  storeId: number,
  filter: string = "all",
  enabled: boolean = true
) => {
  return useQuery({
    queryKey: ["store-financials", storeId, filter],
    queryFn: () =>
      api.get<StoreFinancials>(`store-transaction/financials/byStore?storeId=${storeId}&filter=${filter}`).then(res => res.data),
    enabled: enabled && !!storeId,
  });
};



interface UseParams2 {
  page?: number;
  limit?: number;
  name?: string;
}

export const useStoresNOtDetailedByAdmin = ({
  page = 1,
  limit = 9,
  name = "",
}: UseParams2, enabled: boolean = true) => {
  const queryParams = new URLSearchParams({
    page: page.toString(),
    limit: limit.toString(),
    name: name,
  });
  return useQuery({
    queryKey: ["stores-without-details-by-admin", page, limit, name],
    queryFn: () =>
      api.get<{
        stores: StoreWithoutDetails[];
        total: number;
        totalPages: number;
      }>(`store/admin/all?${queryParams.toString()}`).then(res => res.data),
    enabled,
  });
};

interface UseParams3 {
  page?: number;
  limit?: number;
}
export const useStoreLoginTrackingByAdmin = (
  storeId: number,
  { page = 1, limit = 10 }: UseParams3
) => {
  const queryParams = new URLSearchParams({
    page: page.toString(),
    limit: limit.toString(),
    storeId: storeId.toString(),
  });
  return useQuery({
    queryKey: ["store-login-tracking-by-admin", storeId, page, limit],
    queryFn: () =>
      api.get<{
        total: number;
        totalPages: number;
        logs: { createdAt: string }[];
      }>(`audit-log/store/${storeId}?${queryParams.toString()}`).then(res => res.data),
  });
};

export const useStoreOpenAppTrackingByAdmin = (storeId: number) => {
  return useQuery({
    queryKey: ["store-open-app-tracking-by-admin", storeId],
    queryFn: () =>
      api.get<{ createdAt: string }[]>(`app-open-history/store/${storeId}`).then(res => res.data),
  });
};

interface UseStoresByEventParams {
  eventId: number;
  organizerId: number;
  page?: number;
  limit?: number;
}

export const useStoresByEvent = ({
  eventId,
  organizerId,
  page = 1,
  limit = 9,
}: UseStoresByEventParams) => {
  const queryParams = new URLSearchParams({
    page: page.toString(),
    limit: limit.toString(),
    eventId: eventId.toString(),
    organizerId: organizerId.toString(),
  });
  return useQuery({
    queryKey: ["stores-by-event", eventId, organizerId, page, limit],
    queryFn: () =>
      api.get<{
        stores: StoreWithoutDetails[];
        totalItems: number;
        totalPages: number;
      }>(`store/byEvent?${queryParams.toString()}`).then(res => res.data),
  });
};

// Store Login Link APIs
export interface StoreLoginLink {
  token: string;
  link: string;
}

export const useStoreLoginLink = (
  storeId: number,
  enabled: boolean = true
) => {
  return useQuery({
    queryKey: ["store-login-link", storeId],
    queryFn: () =>
      api.get<StoreLoginLink | null>(`store/login-link/${storeId}`).then(res => res.data),
    enabled: enabled && !!storeId,
  });
};

export const generateStoreLoginLink = (storeId: number) => {
  return apiPost<StoreLoginLink>(`store/generate-login-link/${storeId}`);
};

export const revokeStoreLoginLink = (storeId: number) => {
  return apiDelete<void>(`store/login-link/${storeId}`);
};
